import { useState, useMemo } from 'react';
import { Loader2, Eye, X, MessageSquare, Mail, Phone, Calendar, Search } from 'lucide-react';
import { useContactMessages } from '../../Hooks/useContactMessages';
import { useContactMessageDetails } from '../../Hooks/useContactMessageDetails';
import { canAccess } from '../../utils/permissions';

export default function ContactMessages() {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedMessageId, setSelectedMessageId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const perPage = 15;
    const { messages, pagination, isLoading, error } = useContactMessages(currentPage, perPage);
    const { message: selectedMessage, isLoading: isLoadingDetails, error: detailsError } = useContactMessageDetails(selectedMessageId);

    const closeModal = () => setSelectedMessageId(null);

    const filteredMessages = useMemo(() => {
        if (!searchTerm.trim()) return messages;

        const searchLower = searchTerm.toLowerCase();
        return messages.filter((message) => {
            const fullName = `${message.first_name || ''} ${message.last_name || ''}`.toLowerCase();
            const email = (message.email || '').toLowerCase();

            return fullName.includes(searchLower) || email.includes(searchLower);
        });
    }, [messages, searchTerm]);

    const formatDate = (value) => {
        if (!value) return '—';
        return new Date(value).toLocaleString(undefined, {
            dateStyle: 'long',
            timeStyle: 'short',
        });
    };

    const formatDateShort = (value) => {
        if (!value) return '—';
        return new Date(value).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const truncateMessage = (message, maxLength = 80) => {
        if (!message) return '—';
        if (message.length <= maxLength) return message;
        return message.substring(0, maxLength) + '...';
    };

    if (!canAccess("view_contact_messages")) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <MessageSquare className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
                <p className="text-gray-500 max-w-md font-medium">
                    You don't have the required permissions to view contact messages.
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <section className="min-h-screen text-sm">
                <div className="bg-white border-b border-gray-200 px-4 py-3">
                    <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
                    <p className="text-sm text-gray-500">View all received contact messages</p>
                </div>
                <div className="px-4 py-5">
                    <div className="p-10 text-center">
                        <p className="text-red-600">
                            Error loading contact messages: {error.message || 'Failed to load messages'}
                        </p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="min-h-screen text-sm">
            <div className="bg-white border-b border-gray-200 px-4 py-3">
                <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
                <p className="text-sm text-gray-500">View all received contact messages</p>
            </div>

            <div className="px-4 py-5">
                <div className="mb-4 flex items-center justify-end">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-3 py-2 w-64 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs bg-white"
                        />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden text-xs">
                    {isLoading ? (
                        <div className="p-20 text-center">
                            <Loader2 className="w-12 h-12 mx-auto text-emerald-600 animate-spin" />
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="p-20 text-center text-gray-500">
                            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-lg font-medium">No contact messages available yet.</p>
                            <p className="text-sm mt-2">Messages from your contact form will appear here.</p>
                        </div>
                    ) : filteredMessages.length === 0 ? (
                        <div className="p-20 text-center text-gray-500">
                            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-lg font-medium">No messages found.</p>
                            <p className="text-sm mt-2">Try adjusting your search terms.</p>
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-700">
                                <tr>
                                    <th className="text-left px-3 py-3">Name</th>
                                    <th className="text-left px-3 py-3">Contact</th>
                                    <th className="text-left px-3 py-3">Message</th>
                                    <th className="text-left px-3 py-3">Date</th>
                                    <th className="w-8"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMessages.map((message) => {
                                    const fullName = `${message.first_name || ''} ${message.last_name || ''}`.trim() || 'N/A';
                                    return (
                                        <tr
                                            key={message.id}
                                            className="border-b border-gray-100 hover:bg-gray-50 transition"
                                        >
                                            <td className="px-3 py-3 font-medium text-gray-900">
                                                {fullName}
                                            </td>
                                            <td className="px-3 py-3 text-gray-700">
                                                <div className="flex items-center gap-2 text-xs">
                                                    <Mail className="w-3.5 h-3.5 text-gray-500" />
                                                    <span>{message.email || 'N/A'}</span>
                                                </div>
                                                {message.phone && (
                                                    <div className="flex items-center gap-2 text-xs mt-1">
                                                        <Phone className="w-3.5 h-3.5 text-gray-500" />
                                                        <span>{message.phone}</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 text-gray-700">
                                                <div className="max-w-xs">
                                                    {truncateMessage(message.message)}
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 text-gray-600 flex items-center gap-1">
                                                <Calendar className="w-3.5 h-3.5" />
                                                {formatDateShort(message.created_at)}
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="flex items-center justify-end">
                                                    <button
                                                        onClick={() => setSelectedMessageId(message.id)}
                                                        className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition flex items-center gap-1.5"
                                                    >
                                                        <Eye className="w-3.5 h-3.5" />
                                                        View
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {pagination.last_page > 1 && (
                    <div className="mt-6 flex items-center justify-center gap-2">
                        <button
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition text-sm"
                        >
                            Previous
                        </button>
                        {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-4 py-2 rounded-lg transition text-sm ${page === currentPage
                                    ? 'bg-emerald-600 text-white'
                                    : 'bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pagination.last_page))}
                            disabled={currentPage === pagination.last_page}
                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition text-sm"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {selectedMessageId && (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-[80]" onClick={closeModal} />
                    <div className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-2xl w-full bg-white rounded-2xl shadow-2xl z-[100] max-h-[70vh] overflow-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900">
                                Contact Message Details
                            </h2>
                            <button
                                onClick={closeModal}
                                className="p-2 hover:bg-gray-100 rounded-lg transition"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-8">
                            {isLoadingDetails ? (
                                <div className="flex items-center justify-center py-10">
                                    <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                                </div>
                            ) : detailsError ? (
                                <div className="text-center py-10 text-red-600">
                                    Error loading message details: {detailsError.message || 'Failed to load details'}
                                </div>
                            ) : selectedMessage ? (
                                <>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-3">Contact Information</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                                            <div>
                                                <p className="text-xs text-gray-500">Full Name</p>
                                                <p className="font-medium">
                                                    {`${selectedMessage.first_name || ''} ${selectedMessage.last_name || ''}`.trim() || 'N/A'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500">Email</p>
                                                <p className="font-medium">{selectedMessage.email || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500">Phone</p>
                                                <p className="font-medium">{selectedMessage.phone || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-3">Message</h3>
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <p className="text-gray-700 whitespace-pre-wrap break-words break-all">
                                                {selectedMessage.message || '—'}
                                            </p>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-3">Timestamps</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                                            <div>
                                                <p className="text-xs text-gray-500">Created At</p>
                                                <p className="font-medium">
                                                    {formatDate(selectedMessage.created_at)}
                                                </p>
                                            </div>
                                            {selectedMessage.updated_at && (
                                                <div>
                                                    <p className="text-xs text-gray-500">Updated At</p>
                                                    <p className="font-medium">
                                                        {formatDate(selectedMessage.updated_at)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-10 text-gray-500">
                                    No message details available
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                            <button
                                onClick={closeModal}
                                className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}

